import { isIOS } from "react-device-detect";
import { Link } from "react-router";
import qrCode from "@assets/img-qr-code-app-download.png";
import Button from "@components/Button/Button";
import { ActionType } from "@components/Button/utils/types";
import {
  ANDROID_APP_LINK,
  IOS_APP_LINK,
} from "@components/Footer/utils/constant";
import IconImg from "@components/Icon/IconImg";
import { IconTypeEnum } from "@components/Icon/utils/types";
import Modal from "@components/Modal/Modal";
import { APP_DOWNLOAD_LINKS } from "@features/toolbar/ArticleToolbar/utils/constants";
import useSendDataForTracking from "@hooks/useSendDataForTracking";
import { CommonClassNameAndTestId } from "@typings/Common";

export type ArticleAppBannerProps = {
  /**  Boolean indicating whether the modal is open  */
  isModalOpen: boolean;
  /**  Function to set the modal's open state  */
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /**  Function to handle the modal close action  */
  handleCloseModal: () => void;
  /** title of app banner */
  title?: string;
  /** description of app banner */
  description?: string;
} & CommonClassNameAndTestId;

/**
 * A React component that renders a modal banner for promoting the BT App download.
 * The banner provides options for users to download the app from app stores or via a QR code.
 * It also includes a close button to dismiss the modal.
 */

export default function ArticleAppBanner({
  isModalOpen,
  setIsModalOpen,
  handleCloseModal,
  title = "Download the BT App to enjoy the best reading experience.",
  description = "Download the BT App on your phone.",
}: ArticleAppBannerProps): React.ReactElement {
  const { sendTrackingData } = useSendDataForTracking();

  const handleOnClick = () => {
    if (typeof window === "undefined") return;

    const trackingObject = {
      event: "custom_event",
      eventCategory: "download bt app",
      eventAction: "click",
      eventLabel: window.location.href,
    };

    if (trackingObject) sendTrackingData(trackingObject);
  };

  return (
    <Modal
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      dialogPanelClassNames="absolute left-0 right-0 bottom-0 min-w-full lg:bg-[#121212] rounded-t-[10px]"
      data-testid="article-app-banner-component"
    >
      <div className="relative">
        <Button
          actionType={ActionType.actionButton}
          className="absolute right-6 top-4 hidden border-none lg:block"
          onClick={handleCloseModal}
        >
          <IconImg
            icon={IconTypeEnum.closeWhite}
            imgAlt="play icon"
            iconSize={48}
          />
        </Button>

        <div className="mx-auto w-full max-w-[560px] p-6 text-left text-gray-850 lg:p-8 lg:text-white">
          <p className="mb-2 px-3 font-poppins lg:text-4xl lg:font-semibold">
            {title}
          </p>

          <p className="hidden px-3 font-poppins font-medium tracking-2% lg:block">
            {description}
          </p>

          <div className="hidden w-full divide-x divide-gray-750 border-t border-gray-750 pt-4 lg:flex">
            <div className="flex-1 text-center">
              <p className="font-poppins tracking-2%">Download from stores</p>

              {APP_DOWNLOAD_LINKS.map((link) => (
                <Link
                  to={link.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mx-auto block pb-2"
                  key={link.alt}
                >
                  <img
                    width={135}
                    height={40}
                    src={link.src}
                    alt={link.alt}
                    className="mx-auto"
                  />
                </Link>
              ))}
            </div>

            <div className="flex-1 text-center">
              <p className="font-poppins tracking-2%">Scan the QR code</p>

              <img
                src={qrCode}
                width={160}
                height={160}
                alt="app download"
                className="mx-auto"
              />
            </div>
          </div>

          <div className="block lg:hidden">
            <Link
              to={isIOS ? IOS_APP_LINK : ANDROID_APP_LINK}
              target="blank"
              rel="noopener noreferer"
              className="mb-3 block rounded-full bg-gray-850 px-6 py-4 text-center font-poppins text-xs font-semibold text-white"
              onClick={handleOnClick}
            >
              Download BT App
            </Link>
          </div>
        </div>
      </div>
    </Modal>
  );
}
